import { IAssessmentPackageManagerClient } from './IAssessmentPackageManagerClient';
import { 
    ListAssessmentsSortField,
    CheckFileExistenceInputMessage,
    CheckFileExistenceOutputMessage,
    FeedDownloadedFileInputMessage,
    FeedDownloadedFileOutputMessage,
    ListAssessmentsInputMessage,
    ListAssessmentsOutputMessage,
    LoadAssessmentInputMessage,
    LoadAssessmentOutputMessage,
    AssessmentPackageManagerMessageType,
    FileExistenceStatus
} from '../../../../serviceworker/src/bridges/assessmentPackageManagerMessages';
import { AssessmentPackageMetadata } from '../../../../serviceworker/src/models/AssessmentPackageMetadata';

const swc = ((navigator as Navigator).serviceWorker as ServiceWorkerContainer);
console.log("PWAAssessmentPackageManagerClient: swc", swc);
export default class PWAAssessmentPackageManagerClient implements IAssessmentPackageManagerClient {
    private static readonly DEFAULT_TIMEOUT = 15_000;  // 15s for quick operations
    private static readonly LONG_TIMEOUT = 120_000;    // 120s for feedDownloadedFile/loadAssessment

    private messageHandlers: Map<AssessmentPackageManagerMessageType, { resolve: Function, reject: Function }>;

    constructor() {
        this.messageHandlers = new Map();
        this.initHandlers();
    }

    private initHandlers() {
        swc.addEventListener('message', (event) => {
            const message = event.data;
            if (!Object.values(AssessmentPackageManagerMessageType).includes(message.type)) {
                return; // Not our message
            }
            console.log('PWAAssessmentPackageManagerClient: Received message', message);

            const handler = this.messageHandlers.get(message.type);
            if (handler) {
                console.log('PWAAssessmentPackageManagerClient: Found handler for message', message);
                if ('error' in message) {
                    console.log('PWAAssessmentPackageManagerClient: Error in message', message);
                    handler.reject(new Error(message.error));
                } else {
                    console.log('PWAAssessmentPackageManagerClient: Resolving message', message);
                    handler.resolve(message);
                }
                this.messageHandlers.delete(message.type);
            }
        });
    }

    private async sendMessage<T>(message: any, timeoutMs?: number): Promise<T> {
        if (!swc.controller) {
            throw new Error("No service worker controller available");
        }

        const timeout = timeoutMs ?? PWAAssessmentPackageManagerClient.DEFAULT_TIMEOUT;

        return new Promise((resolve, reject) => {
            const timer = setTimeout(() => {
                this.messageHandlers.delete(message.type);
                reject(new Error(`Service worker timeout after ${timeout}ms for message type: ${message.type}`));
            }, timeout);

            this.messageHandlers.set(message.type, {
                resolve: (value: T) => { clearTimeout(timer); resolve(value); },
                reject: (error: Error) => { clearTimeout(timer); reject(error); }
            });
            swc.controller.postMessage(message);
        });
    }

    async checkFileExistence(filename: string, env: string, package_type:string, assessment_uuid: string, valid_from: string, valid_to: string, school_code: number | null): Promise<FileExistenceStatus> {
        const message = new CheckFileExistenceInputMessage(filename, env, package_type, assessment_uuid, valid_from, valid_to, school_code);
        const response = await this.sendMessage<CheckFileExistenceOutputMessage>(message);
        return response.status;
    }

    async feedDownloadedFile(file: File, metadata: AssessmentPackageMetadata): Promise<number> {
        const message = new FeedDownloadedFileInputMessage(file, metadata);
        const response = await this.sendMessage<FeedDownloadedFileOutputMessage>(message, PWAAssessmentPackageManagerClient.LONG_TIMEOUT);
        return response.assessmentId;
    }

    async listAssessments(sortFields: ListAssessmentsSortField[]): Promise<AssessmentPackageMetadata[]> {
        const message = new ListAssessmentsInputMessage(sortFields);
        const response = await this.sendMessage<ListAssessmentsOutputMessage>(message);
        return response.assessments;
    }

    async loadAssessment(assessmentId: number): Promise<void> {
        const message = new LoadAssessmentInputMessage(assessmentId);
        await this.sendMessage<LoadAssessmentOutputMessage>(message, PWAAssessmentPackageManagerClient.LONG_TIMEOUT);
    }
}
